<template>
  <div class="hike-list-overlay-view">
    <p class="intro">{{ $t('base_map_dialog.hike_list_overlay_view.intro') }}</p>

    <p v-if="!signedIn" class="state-message">{{ $t('base_map_dialog.hike_list_overlay_view.signed_out') }}</p>

    <p v-else-if="hikeListsLoading && !hikeLists?.length" class="state-message">
      {{ $t('base_map_dialog.hike_list_overlay_view.loading') }}
    </p>

    <div v-else-if="hikeListsError && !hikeLists?.length" class="state-message">
      <p class="mb-2">{{ $t('base_map_dialog.hike_list_overlay_view.error') }}</p>
      <button type="button" class="link-btn" @click="reload">{{ $t('base_map_dialog.hike_list_overlay_view.retry') }}</button>
    </div>

    <div v-else-if="hikeLists && hikeLists.length === 0" class="state-message text-center">
      <p class="empty-title mb-1">{{ $t('base_map_dialog.hike_list_overlay_view.empty_title') }}</p>
      <p class="mb-0">{{ $t('base_map_dialog.hike_list_overlay_view.empty_body') }}</p>
    </div>

    <!-- The overlay card in My Trails is the only on/off; lists can be picked either way. -->
    <div v-else-if="hikeLists" class="lists">
      <div class="summary-row">
        <span class="summary">
          {{ $t('base_map_dialog.hike_list_overlay_view.lists_shown', { shown: shownCount, total: selectableCount }) }}
        </span>
        <button type="button" class="link-btn" @click="setAllHikeListsVisible(true)">
          {{ $t('base_map_dialog.hike_list_overlay_view.show_all') }}
        </button>
        <button type="button" class="link-btn" @click="setAllHikeListsVisible(false)">
          {{ $t('base_map_dialog.hike_list_overlay_view.hide_all') }}
        </button>
      </div>

      <label v-if="hikeLists.length > SEARCH_THRESHOLD" class="search">
        <span class="material-icons" aria-hidden="true">search</span>
        <input
          v-model="query"
          type="search"
          :placeholder="$t('base_map_dialog.hike_list_overlay_view.search')"
          :aria-label="$t('base_map_dialog.hike_list_overlay_view.search')"
        />
      </label>

      <p v-if="rows.length === 0" class="state-message">
        {{ $t('base_map_dialog.hike_list_overlay_view.no_matches', { query: query.trim() }) }}
      </p>

      <ul class="rows">
        <li
          v-for="row in rows"
          :key="row.id"
          class="row-item"
          :class="{ 'row-item--hidden': !row.visible, 'row-item--disabled': row.disabled }"
          :aria-disabled="row.disabled ? 'true' : undefined"
        >
          <svg class="swatch" width="40" height="26" viewBox="0 0 40 26" aria-hidden="true">
            <rect width="40" height="26" rx="5" fill="#e6eadc" />
            <path
              d="M5 19 C13 5 25 21 35 7"
              fill="none"
              stroke-linecap="round"
              :stroke="row.prefs.color"
              :stroke-width="Math.max(1.5, 2.4 * row.prefs.thickness)"
              :stroke-opacity="row.visible ? row.prefs.opacity / 100 : 0.25"
            />
          </svg>
          <div class="row-text">
            <div class="row-name">{{ row.name }}</div>
            <div class="row-meta">{{ $t('base_map_dialog.hike_list_overlay_view.hike_count', row.hikesCount) }}</div>
          </div>
          <button
            type="button"
            class="style-btn"
            :disabled="row.disabled"
            :title="$t('base_map_dialog.hike_list_overlay_view.line_style_for', { name: row.name })"
            :aria-label="$t('base_map_dialog.hike_list_overlay_view.line_style_for', { name: row.name })"
            @click="$emit('edit-style', row.id)"
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M12 20h9" /><path d="M16.5 3.5a2.1 2.1 0 0 1 3 3L7 19l-4 1 1-4Z" /></svg>
          </button>
          <input
            class="hl-toggle"
            type="checkbox"
            role="switch"
            :checked="row.visible"
            :disabled="row.disabled"
            :aria-label="$t('base_map_dialog.hike_list_overlay_view.show_named', { name: row.name })"
            @change="setHikeListVisible({ id: row.id, visible: !row.visible })"
          />
        </li>
      </ul>

      <p class="note">
        <span class="material-icons" aria-hidden="true">info</span>
        <span>{{ $t('base_map_dialog.hike_list_overlay_view.multi_list_note') }}</span>
      </p>
    </div>
  </div>
</template>

<script lang="ts">
import { defineComponent } from 'vue';
import { mapActions, mapState } from 'vuex';
import { hikeListRows, selectableHikeListCount, visibleHikeListCount } from '../../../utils/hikeListOverlay';

/**
 * The list picker behind the cog on the "Hike lists" overlay card: show or hide
 * each list and open a list's line style. The card itself is the overlay's only
 * on/off, so lists can be picked whether it is on or not.
 * Rendering rules live in utils/hikeListOverlay.ts; this only lays them out.
 */
export default defineComponent({
  name: 'HikeListOverlayView',

  emits: ['edit-style'],

  data() {
    return {
      query: '',
      // Long enough that scanning by eye gets slow.
      SEARCH_THRESHOLD: 6,
    };
  },

  computed: {
    ...mapState('mapDataOverlays', [
      'hikeLists', 'hikeListsLoading', 'hikeListsError', 'hikeListSelection', 'hikeListStyles',
    ]),
    signedIn(): boolean {
      return !!window.user;
    },
    rows() {
      return hikeListRows(this.hikeLists ?? [], this.hikeListSelection, this.hikeListStyles, this.query);
    },
    shownCount(): number {
      return visibleHikeListCount(this.hikeLists ?? [], this.hikeListSelection);
    },
    selectableCount(): number {
      return selectableHikeListCount(this.hikeLists ?? []);
    },
  },

  mounted() {
    // Lists change elsewhere on the site; refresh whenever the picker opens.
    if (this.signedIn) this.reload();
  },

  methods: {
    ...mapActions('mapDataOverlays', [
      'loadHikeLists', 'applyHikeListOverlay', 'setHikeListVisible', 'setAllHikeListsVisible',
    ]),
    async reload() {
      await this.loadHikeLists({ force: true });
      this.applyHikeListOverlay();
    },
  },
});
</script>

<style scoped>
.hike-list-overlay-view { padding: 16px; padding-bottom: 24px; }
.intro { font-size: 13px; line-height: 1.5; color: #acb0bf; margin-bottom: 14px; }
/* Toggle as in the 2D design: yellow track + dark knob on, grey track + white knob off. */
.hl-toggle {
  appearance: none; -webkit-appearance: none;
  position: relative; flex: 0 0 auto; width: 44px; height: 26px; margin: 0;
  border: 0; border-radius: 100px; background: #505259; cursor: pointer; transition: background 0.15s;
}
.hl-toggle::after {
  content: ''; position: absolute; top: 3px; left: 3px; width: 20px; height: 20px;
  border-radius: 50%; background: #fff; transition: left 0.15s, background 0.15s;
}
.hl-toggle:checked { background: #fddf00; }
.hl-toggle:checked::after { left: 21px; background: #17181d; }
.hl-toggle:focus-visible { outline: 2px solid #fddf00; outline-offset: 2px; }
.hl-toggle:disabled { opacity: 0.5; cursor: default; }
.state-message { font-size: 13px; line-height: 1.5; color: #acb0bf; padding: 20px 4px; margin: 0; }
.empty-title { font-size: 15px; font-weight: 700; color: #fff; }
.summary-row { display: flex; align-items: center; gap: 4px; padding: 14px 0 8px; }
.summary { flex: 1; font-size: 12px; color: #acb0bf; }
.link-btn { border: 0; background: transparent; color: #fddf00; font-size: 12px; font-weight: 700; padding: 6px 8px; }
.link-btn:disabled { color: #717171; }
.search { display: flex; align-items: center; gap: 8px; height: 38px; padding: 0 12px; margin-bottom: 8px; border: 1px solid #505259; border-radius: 8px; background: #2d2f35; color: #acb0bf; }
.search .material-icons { font-size: 18px; }
.search input { flex: 1; min-width: 0; border: 0; background: transparent; color: #fff; font-size: 13px; outline: none; }
.rows { list-style: none; margin: 0; padding: 0; }
.row-item { display: flex; align-items: center; gap: 12px; padding: 8px 4px; border-bottom: 1px solid #2d2f35; }
.row-text { flex: 1; min-width: 0; }
.row-name { font-size: 14px; font-weight: 600; color: #fff; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
.row-item--hidden .row-name { color: #acb0bf; }
.row-meta { font-size: 12px; color: #acb0bf; }
.swatch { flex: 0 0 auto; }
.style-btn { flex: 0 0 auto; width: 36px; height: 36px; border: 0; border-radius: 50%; background: #2d2f35; color: #fff; display: flex; align-items: center; justify-content: center; }
.style-btn:hover:not(:disabled) { background: #3a3a3a; }
.style-btn:disabled { opacity: 0.4; cursor: default; }
/* No hikes: nothing to draw, so the row is greyed out and its controls disabled. */
.row-item--disabled .row-name,
.row-item--disabled .row-meta,
.row-item--disabled .swatch { opacity: 0.45; }
.style-btn .material-icons { font-size: 18px; }
.note { display: flex; gap: 8px; align-items: flex-start; margin: 14px 0 0; padding: 10px 12px; border-radius: 8px; background: #22242a; font-size: 12px; line-height: 1.5; color: #acb0bf; }
.note .material-icons { font-size: 16px; margin-top: 1px; }
</style>
